import { useEffect, useState } from 'react';
import { Lock, Mail, Send, MessageSquare, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useSettings } from '@/lib/settingsContext';
import { useLang } from '@/lib/i18n';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Full-screen lockout shown to users with `isBlocked=true`. Replaces the
 * cabinet content (no nav/sidebar), with admin contacts and a small feedback
 * form so the user can still send a message to support without operating
 * any other endpoints.
 */
export default function BlockedScreen({ user }) {
  const { t } = useLang();
  const settings = useSettings();
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => { document.title = `${t('blocked.title')} | SecretGirls Escort`; }, [t]);

  const adminEmail = settings?.contactEmail || settings?.adminEmail || '';
  const adminTelegram = settings?.adminTelegram || settings?.contactTelegram || '';

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try {
      // Public support form — works without an authed token (it's just a tip)
      await fetch(`${API}/api/support/blocked-feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user?.id || null,
          username: user?.username || '',
          email: user?.email || '',
          message: text.trim(),
        }),
      });
      setSent(true);
      setText('');
      toast.success(t('blocked.sent'));
    } catch {
      toast.error(t('common.error'));
    }
    setSending(false);
  };

  return (
    <div data-testid="blocked-screen"
      className="min-h-[80vh] flex items-center justify-center px-4 py-12">
      <div className="max-w-xl w-full bg-[var(--color-surface)]/60 backdrop-blur-xl border border-red-500/20 rounded-3xl p-8 sm:p-10 text-center">
        <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-red-500/15 flex items-center justify-center">
          <Lock className="w-8 h-8 text-red-400" />
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3 leading-tight">
          {t('blocked.title')}
        </h1>
        <p className="text-sm text-white/60 mb-8 leading-relaxed">
          {t('blocked.subtitle')}
        </p>

        {/* Admin contacts */}
        {(adminEmail || adminTelegram) && (
          <div className="space-y-2 mb-7 text-sm">
            <p className="text-xs uppercase tracking-wider text-white/40 mb-3">{t('blocked.contactsTitle')}</p>
            {adminEmail && (
              <a href={`mailto:${adminEmail}`} data-testid="blocked-email"
                className="flex items-center justify-center gap-2 text-white/80 hover:text-white transition-colors">
                <Mail className="w-4 h-4 text-[var(--color-primary)]" /> {adminEmail}
              </a>
            )}
            {adminTelegram && (
              <a href={`https://t.me/${String(adminTelegram).replace(/^@/, '')}`} target="_blank" rel="noopener noreferrer"
                data-testid="blocked-telegram"
                className="flex items-center justify-center gap-2 text-white/80 hover:text-white transition-colors">
                <Send className="w-4 h-4 text-sky-400" /> @{String(adminTelegram).replace(/^@/, '')}
              </a>
            )}
            <Link to="/kontakt" data-testid="blocked-support-link"
              className="inline-flex items-center justify-center gap-2 mt-2 text-[var(--color-primary)] hover:underline text-sm">
              <MessageSquare className="w-4 h-4" /> {t('blocked.openSupport')}
            </Link>
          </div>
        )}

        {/* Inline feedback form */}
        {!sent ? (
          <form onSubmit={submit} className="text-left space-y-3" data-testid="blocked-feedback-form">
            <label className="text-xs text-white/40 block">{t('blocked.feedbackLabel')}</label>
            <textarea
              data-testid="blocked-feedback-input"
              value={text}
              onChange={(e) => setText(e.target.value.slice(0, 1000))}
              rows={4}
              maxLength={1000}
              placeholder={t('blocked.feedbackPlaceholder')}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[var(--color-primary)]/50 resize-none"
            />
            <button type="submit" disabled={sending || !text.trim()}
              data-testid="blocked-feedback-submit"
              className="w-full bg-[var(--color-primary)] hover:opacity-90 disabled:opacity-40 text-white rounded-xl px-4 py-2.5 text-sm font-medium transition-opacity">
              {sending ? '…' : t('blocked.feedbackSubmit')}
            </button>
          </form>
        ) : (
          <div data-testid="blocked-feedback-sent" className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4 text-sm text-emerald-300">
            {t('blocked.feedbackSentText')}
          </div>
        )}

        <div className="mt-6 pt-4 border-t border-white/5">
          <Link to="/" className="inline-flex items-center gap-2 text-xs text-white/40 hover:text-white/70 transition-colors">
            <ArrowLeft className="w-3.5 h-3.5" /> {t('blocked.backHome')}
          </Link>
        </div>
      </div>
    </div>
  );
}
